import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import OptionVisaComponent from '../components/OptionVisaComponent';
import CarteArriveeChineComponent from '../components/CarteArriveeChineComponent';
import validerDemande from '../validerFormulaire';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  commentaires?: string;
  titleDisplay?: string;
}

const ClassiqueFormChine: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tPays } = useTranslation('formulaires_pays');
  // 1) Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // 2) États initiaux (issus des paramètres ou valeurs par défaut)
  const [title] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(
    state?.prixconsulat || 0
  );
  const [prixintervention, setPrixintervention] = useState<number>(
    state?.prixintervention || 0
  );
  const urlFlag = state?.url_flag || '';
  const validite = state?.validite || '';
  const commentaires = state?.commentaires || '';

  // 3) Champs du formulaire
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<any[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');

  // Motif du voyage (détecté automatiquement depuis le titre)
  const isAffaires = title.toLowerCase().includes('affaires') || title.toLowerCase().includes('travail');
  const isTourisme = title.toLowerCase().includes('tourisme');

  // Documents à télécharger
  const [lettreInvitation, setLettreInvitation] = useState<File | null>(null);
  const [billetsAvion, setBilletsAvion] = useState<File | null>(null);
  const [reservationHotel, setReservationHotel] = useState<File | null>(null);
  const [attestationAssurance, setAttestationAssurance] = useState<File | null>(null);
  const [relevesBancaires, setRelevesBancaires] = useState<File | null>(null);

  // Informations employeur
  const [nomEmployeur, setNomEmployeur] = useState<string>('');
  const [adresseEmployeur, setAdresseEmployeur] = useState<string>('');
  const [telephoneEmployeur, setTelephoneEmployeur] = useState<string>('');
  const [anneeEmbauche, setAnneeEmbauche] = useState<string>('');

  // Diplôme
  const [diplome, setDiplome] = useState<string>('');
  const [nomEtablissementDiplome, setNomEtablissementDiplome] = useState<string>('');

  // Informations père
  const [nomPrenomPere, setNomPrenomPere] = useState<string>('');
  const [nationalitePere, setNationalitePere] = useState<string>('');
  const [dateNaissancePere, setDateNaissancePere] = useState<string>('');

  // Informations mère
  const [nomPrenomMere, setNomPrenomMere] = useState<string>('');
  const [nationaliteMere, setNationaliteMere] = useState<string>('');
  const [dateNaissanceMere, setDateNaissanceMere] = useState<string>('');

  // Contact d'urgence
  const [nomContactUrgence, setNomContactUrgence] = useState<string>('');
  const [telephoneContactUrgence, setTelephoneContactUrgence] = useState<string>('');

  // Visa chinois précédent
  const [visaChinoisPrecedent, setVisaChinoisPrecedent] = useState<string>('');
  const [copieVisaPrecedent, setCopieVisaPrecedent] = useState<File | null>(null);

  // 4) Gestion assurance
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);

  // 5) Services optionnels (cases à cocher)
  const [optionalServices, setOptionalServices] = useState({
    EnlevementParis: false,
    LivraisonParis: false,
    RetourDHL: false,
    TraitementVIP: false,
    SaisieFormulaire: false,
  });

  // 5 bis) Carte d'arrivée (e-arrival Card) - spécifique Chine
  const [carteArrivee, setCarteArrivee] = useState<string>('');
  const [billetAvionECard, setBilletAvionECard] = useState<File | null>(null);
  const [pasDeBilletECard, setPasDeBilletECard] = useState<boolean>(false);
  const [hotelECard, setHotelECard] = useState<File | null>(null);
  const [pasDeHotelECard, setPasDeHotelECard] = useState<boolean>(false);

  // 6) Liste des champs obligatoires manquants
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 7) États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [hideFees] = useState<boolean>(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // 8) Fonction pour calculer la somme des services optionnels
  const calculateOptionalServicesTotal = (): number => {
    let sum = 0;
    if (optionalServices.EnlevementParis) sum += 49;    // Enlèvement Paris
    if (optionalServices.LivraisonParis) sum += 49;     // Livraison Paris
    if (optionalServices.RetourDHL) sum += 49;          // Retour DHL
    if (optionalServices.TraitementVIP) sum += 49;      // Traitement VIP
    if (optionalServices.SaisieFormulaire) sum += 39;   // Saisie formulaire
    if (carteArrivee === 'oui') sum += 39;              // Carte d'arrivée (e-arrival Card)
    return sum;
  };

  // 8) Calcul du total
  //    On additionne: frais consulat + frais service + assurance éventuelle + options
  const total =
    prixconsulat +
    prixintervention +
    (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0) +
    calculateOptionalServicesTotal();

  // 9) Mise à jour des champs non remplis selon les valeurs saisies
  const updateChampsNonRemplis = () => {
    const champs: string[] = [];

    if (!selectedVoyageur) champs.push(t('common.validation.traveler'));
    if (!dateDepart.trim()) champs.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) champs.push(t('common.validation.returnDate'));

    // Documents selon le motif (détecté depuis le titre)
    if (isAffaires && !lettreInvitation) {
      champs.push(tPays('chine.validation.lettreInvitation'));
    }
    if (isTourisme) {
      if (!billetsAvion) champs.push(tPays('chine.validation.billetsAvion'));
      if (!reservationHotel) champs.push(tPays('chine.validation.reservationHotel'));
      if (!attestationAssurance) champs.push(tPays('chine.validation.attestationAssurance'));
      if (!relevesBancaires) champs.push(tPays('chine.validation.relevesBancaires'));
    }

    // Informations employeur
    if (!nomEmployeur.trim()) champs.push(tPays('chine.validation.nomEmployeur'));
    if (!adresseEmployeur.trim()) champs.push(tPays('chine.validation.adresseEmployeur'));
    if (!telephoneEmployeur.trim()) champs.push(tPays('chine.validation.telephoneEmployeur'));
    if (!anneeEmbauche.trim()) champs.push(tPays('chine.validation.anneeEmbauche'));

    // Diplôme
    if (!diplome) champs.push(tPays('chine.validation.diplome'));
    if (!nomEtablissementDiplome.trim()) champs.push(tPays('chine.validation.nomEtablissementDiplome'));

    // Informations père
    if (!nomPrenomPere.trim()) champs.push(tPays('chine.validation.nomPrenomPere'));
    if (!nationalitePere.trim()) champs.push(tPays('chine.validation.nationalitePere'));
    if (!dateNaissancePere.trim()) champs.push(tPays('chine.validation.dateNaissancePere'));

    // Informations mère
    if (!nomPrenomMere.trim()) champs.push(tPays('chine.validation.nomPrenomMere'));
    if (!nationaliteMere.trim()) champs.push(tPays('chine.validation.nationaliteMere'));
    if (!dateNaissanceMere.trim()) champs.push(tPays('chine.validation.dateNaissanceMere'));

    // Contact d'urgence
    if (!nomContactUrgence.trim()) champs.push(tPays('chine.validation.nomContactUrgence'));
    if (!telephoneContactUrgence.trim()) champs.push(tPays('chine.validation.telephoneContactUrgence'));

    // Visa chinois précédent
    if (!visaChinoisPrecedent) champs.push(tPays('chine.validation.visaChinoisPrecedent'));
    if (visaChinoisPrecedent === 'oui' && !copieVisaPrecedent) {
      champs.push(tPays('chine.validation.copieVisaPrecedent'));
    }

    // Carte d'arrivée (e-arrival Card)
    if (!carteArrivee) {
      champs.push(tPays('chine.validation.carteArrivee'));
    }
    if (carteArrivee === 'oui' && !isTourisme) {
      // En tourisme, billet d'avion et réservation hôtel sont déjà demandés plus haut
      if (!billetAvionECard && !pasDeBilletECard) {
        champs.push(tPays('chine.validation.billetAvionECard'));
      }
      if (!hotelECard && !pasDeHotelECard) {
        champs.push(tPays('chine.validation.hotelECard'));
      }
    }

    if (!assuranceVisa.trim()) champs.push(t('common.validation.insurance'));
    if (!sliderValue) {
      champs.push(t('common.validation.cguAcceptance'));
    }
    setChampsNonRemplis(champs);
  };

  // 10) Vérification globale du formulaire
  const isFormValid = champsNonRemplis.length === 0;

  // 11) Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(
          user.id_utilisateur
        );
        setVoyageurs(listeVoyageurs);
        // Si un seul voyageur, on le sélectionne d'office
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // 12) Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  // 13) Met à jour la liste des champs non remplis à chaque changement
  useEffect(() => {
    updateChampsNonRemplis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVoyageur,
    dateDepart,
    dateSortie,
    lettreInvitation,
    billetsAvion,
    reservationHotel,
    attestationAssurance,
    relevesBancaires,
    nomEmployeur,
    adresseEmployeur,
    telephoneEmployeur,
    anneeEmbauche,
    diplome,
    nomEtablissementDiplome,
    nomPrenomPere,
    nationalitePere,
    dateNaissancePere,
    nomPrenomMere,
    nationaliteMere,
    dateNaissanceMere,
    nomContactUrgence,
    telephoneContactUrgence,
    visaChinoisPrecedent,
    copieVisaPrecedent,
    assuranceVisa,
    sliderValue,
    optionalServices,
    carteArrivee,
    billetAvionECard,
    pasDeBilletECard,
    hotelECard,
    pasDeHotelECard,
  ]);

  // 14) Récupération éventuelle du voyageur sélectionné pour l'afficher (avatar)
  const selectedVoyageurObj = voyageurs.find(
    (v: any) => v.id.toString() === selectedVoyageur
  );

  // 14) Empêche de quitter la page sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // 15) Formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string): string => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // 16) Gestion du bouton "Retour"
  const handleBack = () => {
    if (window.confirm(t('common.confirmation.unsavedChanges'))) {
      navigate(-1);
    }
  };

  // 17) Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = isFormValid;

      // Exemple : Si vous souhaitez enregistrer le total des options
      // dans vos "frais d'intervention", vous pouvez le faire ici
      const prixInterventionAvecOptions =
        prixintervention + calculateOptionalServicesTotal();

      // Données à envoyer au backend
      const data = {
        etatFormulaire,
        idUtilisateur: userId,
        idVoyageur: selectedVoyageur,
        titreVisa: title,
        dateDepart,
        dateSortie,
        prixconsulat,
        // On modifie ou non selon votre logique de backend :
        prixintervention: prixInterventionAvecOptions,
        assuranceVisa,
        optionalServices,
        optionalServicesTotal: calculateOptionalServicesTotal(),
        validite,
        // Nouveaux champs Chine
        motifVoyage: isAffaires ? 'Affaires' : isTourisme ? 'Tourisme' : '',
        lettreInvitation,
        billetsAvion,
        reservationHotel,
        attestationAssurance,
        relevesBancaires,
        nomEmployeur,
        adresseEmployeur,
        telephoneEmployeur,
        anneeEmbauche,
        diplome,
        nomEtablissementDiplome,
        nomPrenomPere,
        nationalitePere,
        dateNaissancePere,
        nomPrenomMere,
        nationaliteMere,
        dateNaissanceMere,
        nomContactUrgence,
        telephoneContactUrgence,
        visaChinoisPrecedent,
        copieVisaPrecedent,
        // Carte d'arrivée (e-arrival Card) - spécifique Chine
        carteArrivee,
        // Si motif Tourisme et carte d'arrivée = oui, on réutilise billetsAvion et reservationHotel
        // déjà uploadés plus haut. Sinon (Affaires ou autre), on envoie les uploads spécifiques.
        billetAvionECard: !isTourisme ? billetAvionECard : null,
        pasDeBilletECard: !isTourisme ? pasDeBilletECard : false,
        hotelECard: !isTourisme ? hotelECard : null,
        pasDeHotelECard: !isTourisme ? pasDeHotelECard : false,
      };

      const response = await validerDemande(data);
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.idDemande) {
        setCreatedDemandeId(response.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // 18) Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // 19) Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setOptionalServices({
      EnlevementParis: false,
      LivraisonParis: false,
      RetourDHL: false,
      TraitementVIP: false,
      SaisieFormulaire: false,
    });
    // Réinitialiser les nouveaux champs Chine
    setLettreInvitation(null);
    setBilletsAvion(null);
    setReservationHotel(null);
    setAttestationAssurance(null);
    setRelevesBancaires(null);
    setNomEmployeur('');
    setAdresseEmployeur('');
    setTelephoneEmployeur('');
    setAnneeEmbauche('');
    setDiplome('');
    setNomEtablissementDiplome('');
    setNomPrenomPere('');
    setNationalitePere('');
    setDateNaissancePere('');
    setNomPrenomMere('');
    setNationaliteMere('');
    setDateNaissanceMere('');
    setNomContactUrgence('');
    setTelephoneContactUrgence('');
    setVisaChinoisPrecedent('');
    setCopieVisaPrecedent(null);
    setCarteArrivee('');
    setBilletAvionECard(null);
    setPasDeBilletECard(false);
    setHotelECard(null);
    setPasDeHotelECard(false);
  };

  // 20) Action quand le formulaire est invalide
  const disabledPressHandler = () => {
    // Par exemple, vous pouvez afficher un message ou un alert
    // alert("Formulaire incomplet");
  };

  // 21) Rendu du composant
  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          {/* ---------- En-tête ---------- */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* ---------- Sélection du voyageur (si pas en mode édition) ---------- */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">
                  {t('common.selectTraveler')}
                </InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((voyageur: any) => (
                    <MenuItem key={voyageur.id} value={voyageur.id.toString()}>
                      {voyageur.prenom} {voyageur.nom}
                      {voyageur.numeropax ? t('common.selectTravelerWithPassport', { passport: voyageur.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* ---------- Avatar du voyageur sélectionné ---------- */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={
                  selectedVoyageurObj.url ||
                  'https://example.com/default-avatar.png'
                }
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* ---------- Dates départ / retour ---------- */}
          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* ---------- Documents à télécharger selon le motif ---------- */}
          {isAffaires && (
            <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                {tPays('chine.documents.sectionTitleAffaires')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tPays('chine.documents.lettreInvitation')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setLettreInvitation(e.target.files?.[0] || null)}
                />
                {lettreInvitation && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {lettreInvitation.name}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {isTourisme && (
            <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                {tPays('chine.documents.sectionTitleTourisme')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tPays('chine.documents.billetsAvion')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setBilletsAvion(e.target.files?.[0] || null)}
                />
                {billetsAvion && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {billetsAvion.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tPays('chine.documents.reservationHotel')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setReservationHotel(e.target.files?.[0] || null)}
                />
                {reservationHotel && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {reservationHotel.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tPays('chine.documents.attestationAssurance')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setAttestationAssurance(e.target.files?.[0] || null)}
                />
                {attestationAssurance && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {attestationAssurance.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tPays('chine.documents.relevesBancaires')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setRelevesBancaires(e.target.files?.[0] || null)}
                />
                {relevesBancaires && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {relevesBancaires.name}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* ---------- Informations employeur ---------- */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tPays('chine.sections.informationsProfessionnelles')}
            </Typography>
            <TextField
              label={tPays('chine.fields.nomEmployeur')}
              value={nomEmployeur}
              onChange={(e) => setNomEmployeur(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.adresseEmployeur')}
              value={adresseEmployeur}
              onChange={(e) => setAdresseEmployeur(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.telephoneEmployeur')}
              value={telephoneEmployeur}
              onChange={(e) => setTelephoneEmployeur(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.anneeEmbauche.label')}
              value={anneeEmbauche}
              onChange={(e) => setAnneeEmbauche(e.target.value)}
              fullWidth
              margin="normal"
              placeholder={tPays('chine.fields.anneeEmbauche.placeholder')}
            />
          </Box>

          {/* ---------- Diplôme ---------- */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tPays('chine.sections.formation')}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel id="diplome-label">{tPays('chine.fields.diplome')}</InputLabel>
              <Select
                labelId="diplome-label"
                value={diplome}
                label={tPays('chine.fields.diplome')}
                onChange={(e) => setDiplome(e.target.value)}
              >
                <MenuItem value="Ecole secondaire technique / lycée ou équivalent">
                  {tPays('chine.diplomeOptions.ecoleSecondaire')}
                </MenuItem>
                <MenuItem value="Master ou équivalent">{tPays('chine.diplomeOptions.master')}</MenuItem>
                <MenuItem value="Doctorat ou supérieur">{tPays('chine.diplomeOptions.doctorat')}</MenuItem>
                <MenuItem value="Autre">{tPays('chine.diplomeOptions.autre')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={tPays('chine.fields.nomEtablissementDiplome')}
              value={nomEtablissementDiplome}
              onChange={(e) => setNomEtablissementDiplome(e.target.value)}
              fullWidth
              margin="normal"
            />
          </Box>

          {/* ---------- Informations père ---------- */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tPays('chine.sections.informationsPere')}
            </Typography>
            <TextField
              label={tPays('chine.fields.nomPrenomPere')}
              value={nomPrenomPere}
              onChange={(e) => setNomPrenomPere(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.nationalitePere')}
              value={nationalitePere}
              onChange={(e) => setNationalitePere(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.dateNaissancePere.label')}
              placeholder={tPays('chine.fields.dateNaissancePere.placeholder')}
              value={dateNaissancePere}
              onChange={(e) => setDateNaissancePere(formatDate(e.target.value))}
              fullWidth
              margin="normal"
            />
          </Box>

          {/* ---------- Informations mère ---------- */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tPays('chine.sections.informationsMere')}
            </Typography>
            <TextField
              label={tPays('chine.fields.nomPrenomMere')}
              value={nomPrenomMere}
              onChange={(e) => setNomPrenomMere(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.nationaliteMere')}
              value={nationaliteMere}
              onChange={(e) => setNationaliteMere(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.dateNaissanceMere.label')}
              placeholder={tPays('chine.fields.dateNaissanceMere.placeholder')}
              value={dateNaissanceMere}
              onChange={(e) => setDateNaissanceMere(formatDate(e.target.value))}
              fullWidth
              margin="normal"
            />
          </Box>

          {/* ---------- Contact d'urgence ---------- */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tPays('chine.sections.contactUrgence')}
            </Typography>
            <TextField
              label={tPays('chine.fields.nomContactUrgence')}
              value={nomContactUrgence}
              onChange={(e) => setNomContactUrgence(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label={tPays('chine.fields.telephoneContactUrgence')}
              value={telephoneContactUrgence}
              onChange={(e) => setTelephoneContactUrgence(e.target.value)}
              fullWidth
              margin="normal"
            />
          </Box>

          {/* ---------- Visa chinois précédent ---------- */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tPays('chine.sections.visaChinoisPrecedent')}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel id="visa-precedent-label">
                {tPays('chine.fields.visaChinoisPrecedent')}
              </InputLabel>
              <Select
                labelId="visa-precedent-label"
                value={visaChinoisPrecedent}
                label={tPays('chine.fields.visaChinoisPrecedent')}
                onChange={(e) => setVisaChinoisPrecedent(e.target.value)}
              >
                <MenuItem value="oui">{t('common.yes')}</MenuItem>
                <MenuItem value="non">{t('common.no')}</MenuItem>
              </Select>
            </FormControl>
            {visaChinoisPrecedent === 'oui' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tPays('chine.visaPrecedent.uploadLabel')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'gray' }}>
                  {tPays('chine.visaPrecedent.uploadHelp')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setCopieVisaPrecedent(e.target.files?.[0] || null)}
                />
                {copieVisaPrecedent && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {copieVisaPrecedent.name}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* ---------- Carte d'arrivée (e-arrival Card) ---------- */}
          <Box sx={{ my: 2 }}>
            <CarteArriveeChineComponent
              carteArrivee={carteArrivee}
              setCarteArrivee={setCarteArrivee}
              reuseBilletHotel={isTourisme}
              billetAvionECard={billetAvionECard}
              setBilletAvionECard={setBilletAvionECard}
              pasDeBilletECard={pasDeBilletECard}
              setPasDeBilletECard={setPasDeBilletECard}
              hotelECard={hotelECard}
              setHotelECard={setHotelECard}
              pasDeHotelECard={pasDeHotelECard}
              setPasDeHotelECard={setPasDeHotelECard}
            />
          </Box>

           {/* ---------- Services optionnels ---------- */}
          <Box sx={{ my: 2 }}>
            <OptionVisaComponent
              optionalServices={optionalServices}
              setOptionalServices={setOptionalServices}
            />
          </Box>

          {/* ---------- Assurance & CGV ---------- */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>



          {/* ---------- Affichage des frais et total ---------- */}
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Typography variant="body1">
              {t('common.fees.visa', { prix: prixconsulat })}
            </Typography>
            <Typography variant="body1">
              {t('common.fees.service', { prix: prixintervention })}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {assuranceVisa.toLowerCase() === 'oui'
                ? t('common.fees.insuranceYes')
                : t('common.fees.insuranceNo')}
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {t('common.fees.total', { total })}
            </Typography>
          </Box>

          {/* ---------- Indicateur de formulaire complet / incomplet ---------- */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor:
                champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* ---------- Bouton de validation ---------- */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ClassiqueFormChine;
